// Role names and the policy sets the server enforces (docs/contracts/ROLES_AND_POLICIES.md §2, and
// src/api/SummitOperations.Api/Auth/OperationsRoles.cs). In the SPA these only decide what to SHOW — the API is the
// control. WallDisplay is deliberately not an Operations principal: the kiosk reaches exactly one board endpoint.

export const Roles = {
  /** Michael Sharpe (+ delegate). Replaces Summit Engineering's EngineeringManager. */
  OperationsManager: "OperationsManager",
  /** The Planners (Q-O1). Priorities, pegs, scenarios, assignments; publishes baselines. */
  Planner: "Planner",
  /** Shop and cell leads (Q-O2). Downtime, machine status, dispatch and hotlist notes. */
  ShopSupervisor: "ShopSupervisor",
  /** Anyone in Operations who needs to look. Reads; changes nothing. */
  OperationsViewer: "OperationsViewer",
  /** Kiosk devices. Not an Operations principal — refused by the default-deny fallback. */
  WallDisplay: "WallDisplay",
  Admin: "Admin",
  /** In-app grant, alias of Admin (the server adds Admin to these roles, so the SPA's Admin checks apply unchanged). */
  ITAdministrator: "ITAdministrator",
  /** In-app grant. Final approver for controlled content. */
  PlantManager: "PlantManager",
  ShippingManager: "ShippingManager",
  PurchasingManager: "PurchasingManager",
  /** In-app grant (D-O67). The purchasers: review, approve (signed) and print work-order routers; Purchasing workspace only. */
  Purchasing: "Purchasing",
  /** In-app grant. Leadership, read-only. */
  Executive: "Executive",
  /** In-app grant. QA/QC, read-only auditor. */
  Quality: "Quality",
  /** In-app grant. QAM/DOQ co-approval of controlled documents. */
  QualityApprover: "QualityApprover",
  /** In-app grant. Cross-app read for the Engineering Manager (Q-O23 default: yes, read-only). */
  EngineeringManager: "EngineeringManager",
  /** Release 1.2.0. Everything a Planner may do (the server adds Planner) plus publishing the baseline and frozen-zone approval. */
  MasterScheduler: "MasterScheduler",
  /** Release 1.9.0 (D-O151): in-app grant. Shipping staff: Logistics inbox, shipments, labels (signed). */
  Logistics: "Logistics"
} as const;

export type Role = (typeof Roles)[keyof typeof Roles];

/**
 * Every role that may use the application as a person (the server's `OperationsRoles.OperationsPrincipals`).
 * WallDisplay is deliberately absent.
 */
export const OperationsPrincipals: readonly Role[] = [
  Roles.OperationsManager,
  Roles.Planner,
  Roles.ShopSupervisor,
  Roles.OperationsViewer,
  Roles.Admin,
  Roles.ITAdministrator,
  Roles.PlantManager,
  Roles.Executive,
  Roles.Quality,
  Roles.QualityApprover,
  Roles.EngineeringManager,
  Roles.ShippingManager,
  Roles.PurchasingManager,
  Roles.Purchasing,
  Roles.MasterScheduler,
  Roles.Logistics
];

/** Everyone who takes part — the principals without the read-only Executive and the Purchasing-only role (D-O67). */
export const Participants: readonly Role[] = OperationsPrincipals.filter((r) => r !== Roles.Executive && r !== Roles.Purchasing && r !== Roles.Logistics);

/** Policy `OperationsData`: all non-personal operations reads — hub, schedule, trees, LOB, WIP, capacity, dispatch. */
export const OperationsData: readonly Role[] = [
  Roles.OperationsManager,
  Roles.Planner,
  Roles.ShopSupervisor,
  Roles.OperationsViewer,
  Roles.PlantManager,
  Roles.Executive,
  Roles.Admin,
  Roles.EngineeringManager,
  Roles.Quality,
  Roles.ShippingManager,
  Roles.PurchasingManager,
  // Release 1.4.0 (owner: "purchasers should be able to deal with the work orders"): reads only, no edits.
  Roles.Purchasing
];

/** Policy `SchedulePlanners`: priorities, pegs, scenarios, assignments, draft capacity overrides, hotlist edit. */
export const SchedulePlanners: readonly Role[] = [Roles.Planner, Roles.OperationsManager, Roles.Admin];

/** Policy `SchedulePublishers`: publish a baseline (signed, chain of custody). */
export const SchedulePublishers: readonly Role[] = [Roles.Planner, Roles.OperationsManager, Roles.MasterScheduler];

/** Policy `ScheduleOverriders`: frozen-zone override, capacity-override approval (signed). */
export const ScheduleOverriders: readonly Role[] = [Roles.OperationsManager, Roles.MasterScheduler];

/** Policy `MasterScheduleControl` (release 1.2.0): the Master schedule control screen. */
export const MasterScheduleControl: readonly Role[] = [Roles.MasterScheduler, Roles.Planner, Roles.OperationsManager, Roles.Admin];

/** Policy `ShopFloorEditors`: downtime log, machine status, hotlist notes, dispatch notes. */
export const ShopFloorEditors: readonly Role[] = [Roles.ShopSupervisor, Roles.Planner, Roles.OperationsManager, Roles.Admin];

/** Policy `MachineRegistryEditors`. */
export const MachineRegistryEditors: readonly Role[] = [Roles.OperationsManager, Roles.Planner, Roles.Admin];

/** Policy `PerPersonReaders`: load board, efficiency, skills — the OM, the Planner and Admin see every employee (Q-O13, D-O60). */
export const PerPersonReaders: readonly Role[] = [Roles.OperationsManager, Roles.Planner, Roles.Admin];

/** Policy `ManagementReaders`: weekly report, executive board, adherence. */
export const ManagementReaders: readonly Role[] = [Roles.OperationsManager, Roles.PlantManager, Roles.Executive, Roles.Admin];

/** Policy `ComplianceReaders`: AS9100 and CMMC self-assessments. */
export const ComplianceReaders: readonly Role[] = [Roles.OperationsManager, Roles.PlantManager, Roles.Quality, Roles.Executive, Roles.Admin];

/** Policy `AuditReaders`: the audit log. */
export const AuditReaders: readonly Role[] = [Roles.Admin, Roles.Quality];

/** Policy `CustodyReaders`: who signed what. */
export const CustodyReaders: readonly Role[] = [
  Roles.OperationsManager,
  Roles.Planner,
  Roles.PlantManager,
  Roles.Quality,
  Roles.QualityApprover,
  Roles.Executive,
  Roles.Admin
];

/** Policy `DocumentApprovers`: the Operations Manager submits, Quality approves, the Plant Manager finalises. */
export const DocumentApprovers: readonly Role[] = [Roles.OperationsManager, Roles.QualityApprover, Roles.PlantManager, Roles.Admin];

/** Policy `FinalApprovers`. */
export const FinalApprovers: readonly Role[] = [Roles.PlantManager, Roles.OperationsManager];

/** Policy `Contributors`: knowledge drafts and incident reports. */
export const Contributors: readonly Role[] = [Roles.OperationsManager, Roles.Planner, Roles.ShopSupervisor, Roles.Admin];

/** Policy `ViperOperators`: VIPER scenario and rebalance tools. Everyone else gets read-only answers. */
export const ViperOperators: readonly Role[] = [Roles.OperationsManager, Roles.Planner, Roles.Admin];

/** Policy `AdminOnly`. ITAdministrator reaches this through the server-side alias to Admin. */
export const AdminOnly: readonly Role[] = [Roles.Admin];

/** Policy `GovernedOperations`: the Operations subset of governed settings the Operations Manager may edit. */
export const GovernedOperations: readonly Role[] = [Roles.OperationsManager, Roles.Admin];

/** Policy `WallDisplayAccess`: the kiosk principal plus the people who set the boards up. */
export const WallDisplayAccess: readonly Role[] = [Roles.WallDisplay, Roles.OperationsManager, Roles.Admin];

/** Who may configure a wall display from inside the application — the kiosk role itself never sees a menu. */
export const WallDisplayManagers: readonly Role[] = [Roles.OperationsManager, Roles.Admin];

export const RoleLabels: Record<Role, string> = {
  OperationsManager: "Operations Manager",
  Planner: "Planner",
  ShopSupervisor: "Shop Supervisor",
  OperationsViewer: "Operations Viewer",
  WallDisplay: "Wall Display",
  Admin: "Admin",
  ITAdministrator: "IT Administrator",
  PlantManager: "Plant Manager",
  ShippingManager: "Shipping Manager (Logistics)",
  PurchasingManager: "Purchasing Manager",
  // Release 1.4.0: the role value stays "Purchasing"; people see "Purchaser".
  Purchasing: "Purchaser",
  Executive: "Executive (read-only)",
  Quality: "Quality",
  QualityApprover: "Quality approver (QAM/DOQ)",
  EngineeringManager: "Engineering Manager (read-only)",
  MasterScheduler: "Master Scheduler",
  Logistics: "Logistics (shipping)"
};

/**
 * The read-only Executive's menu: leadership looks at the picture and changes nothing (Summit Engineering precedent,
 * carried over). Everything else is refused by the API anyway, so the menu simply agrees with it.
 */
export const ExecutiveMenu: readonly string[] = [
  "/my/notifications",
  "/operations",
  "/operations/line-of-balance",
  "/operations/wip",
  "/operations/alerts",
  "/operations/weekly-report",
  "/shop-floor/sqdc",
  "/knowledge/base",
  "/knowledge/procedures",
  "/knowledge/incidents",
  "/quality/as9100",
  "/quality/cmmc",
  "/viper",
  "/about/release-notes",
  "/about/architecture",
  "/about/ai-ethics",
  "/about/ai-policies"
];

export function hasAnyRole(roles: readonly string[] | undefined, allowed: readonly Role[]): boolean {
  return !!roles && roles.some((r) => (allowed as readonly string[]).includes(r));
}

/**
 * The Purchaser's menu (release 1.4.0): their workspace first, the work orders, material & OSP, the OSP board and the
 * Purchasing view, plus their own notifications, profile, feedback, the knowledge base and About. They may READ the other
 * Operations screens (policy OperationsData) — the menu simply keeps to what they need.
 */
export const PurchaserMenu: readonly string[] = [
  "/purchasing",
  "/operations/work-orders",
  "/shop-floor/material",
  "/shop-floor/osp",
  "/my/notifications",
  "/profile",
  "/my/feedback",
  "/knowledge/base",
  "/knowledge/procedures",
  "/knowledge/incidents",
  "/training",
  "/about/release-notes",
  "/about/architecture",
  "/about/ai-ethics",
  "/about/ai-policies",
  "/about/pilot-guide"
];

/** A Purchaser and nothing else that uses the application (release 1.4.0): they get the Purchaser menu. */
export function isPurchaserOnly(roles: readonly string[] | undefined): boolean {
  return !!roles && roles.includes(Roles.Purchasing) && !roles.some((r) => r !== Roles.Purchasing && (OperationsPrincipals as readonly string[]).includes(r));
}

/** The Executive and nothing that takes part: the owner's menu only, and no add/change buttons anywhere. */
export function isReadOnlyExecutive(roles: readonly string[] | undefined): boolean {
  return !!roles && roles.includes(Roles.Executive) && !hasAnyRole(roles, Participants);
}

/** A read-only person who must not see add/change buttons: Quality or the Executive with no contributing role. */
export function isReadOnlyAuditor(roles: readonly string[] | undefined): boolean {
  return !!roles && (roles.includes(Roles.Quality) || roles.includes(Roles.Executive)) && !hasAnyRole(roles, Contributors);
}

/** Per-person data (load, efficiency, skills) is for the PerPersonReaders and the person themself (Q-O13). */
export function seesPerPersonData(roles: readonly string[] | undefined): boolean {
  return hasAnyRole(roles, PerPersonReaders);
}

/** The kiosk principal: signed in, but it reaches exactly one aggregated board endpoint and never a menu. */
export function isWallDisplayOnly(roles: readonly string[] | undefined): boolean {
  return !!roles && roles.includes(Roles.WallDisplay) && !hasAnyRole(roles, OperationsPrincipals);
}

/** Release 1.9.0 policy `LogisticsReaders`: Logistics inbox, shipments, Coming & Going dashboard, reports. */
export const LogisticsReaders: readonly Role[] = [
  Roles.Logistics,
  Roles.ShippingManager,
  Roles.Purchasing,
  Roles.PurchasingManager,
  Roles.OperationsManager,
  Roles.Planner,
  Roles.PlantManager,
  Roles.Executive,
  Roles.Admin
];

/** Release 1.9.0 policy `LogisticsShippers`: record shipments, buy labels (signed), void. */
export const LogisticsShippers: readonly Role[] = [Roles.Logistics, Roles.ShippingManager, Roles.Admin];
/** Release 1.11.0 policy `PurchasingApprovers`: create RFQs, add vendors, send, record quotes, award (signed), advance OSP. */
export const PurchasingApprovers: readonly Role[] = [Roles.Purchasing, Roles.PurchasingManager];
